/** The Broadcasts list's two tabs, by the URL segment each is served at. */
export type BroadcastsTab = 'one_time_sends' | 'api-triggered';

/** The tab a bare `/broadcasts` lands on when there is no earlier one to return to. */
export const DEFAULT_BROADCASTS_TAB: BroadcastsTab = 'one_time_sends';

export const BROADCASTS_TAB_LABELS = {
  one_time_sends: 'One-time sends',
  'api-triggered': 'API-triggered broadcasts',
} as const satisfies Record<BroadcastsTab, string>;

/** The migrated route each tab is served at. */
export const BROADCASTS_TAB_ROUTES = {
  one_time_sends: '/workspaces/$workspaceId/broadcasts/one_time_sends',
  'api-triggered': '/workspaces/$workspaceId/broadcasts/api-triggered',
} as const satisfies Record<BroadcastsTab, string>;

/** Which tab a migrated Broadcasts pathname is on, or `null` for the bare `/broadcasts`. */
export function broadcastsTabFromPathname(pathname: string): BroadcastsTab | null {
  if (/\/broadcasts\/one_time_sends\/?$/.test(pathname)) {
    return 'one_time_sends';
  }
  if (/\/broadcasts\/api-triggered\/?$/.test(pathname)) {
    return 'api-triggered';
  }
  return null;
}

/**
 * The tab the reader was last on, for as long as the app stays loaded.
 *
 * A bare `/broadcasts` returns to it, including after leaving for another page and coming
 * back, which is why it lives here rather than in the page's own state: the page's state goes
 * when the page does, and this has to outlive it. A reload starts over at the default tab.
 */
let lastBroadcastsTab: BroadcastsTab | null = null;

export function rememberBroadcastsTab(tab: BroadcastsTab): void {
  lastBroadcastsTab = tab;
}

/**
 * The tab a bare `/broadcasts` sends the reader to. A Builder account has only the one-time
 * sends tab (the campaigns endpoint behind the other refuses it), so that is always where it
 * goes.
 */
export function landingBroadcastsTab({ isBuilder }: { isBuilder: boolean }): BroadcastsTab {
  if (isBuilder) {
    return 'one_time_sends';
  }
  return lastBroadcastsTab ?? DEFAULT_BROADCASTS_TAB;
}
